/**
 * Truy cập dữ liệu của module `tham-dinh` (Thẩm định AI), ADR-0080.
 *
 * Gọi thẳng API thật — module này **không có máy chủ giả**: tầng `src/mock/` sẽ bị xoá khi API thật về
 * (CLAUDE.md), và dựng mock cho một module vừa viết backend là tạo ra hai nguồn sự thật phải giữ khớp nhau.
 * Hệ quả cần biết: soát bốn trạng thái của những màn này không dùng `?mock=` được, phải chạy module thật.
 *
 * Đường dẫn giữ nguyên qua Gateway (ADR-0051 mục 2): `/api/v1/m/tham-dinh/...` tới thẳng module.
 *
 * Cả năm nhóm có backend thật (23/10/2026). Hồ sơ nộp lên được chấm ở container worker riêng (ADR-0069) nên
 * `POST /phien` trả về ngay với phiên ở `cho_xu_ly`; giao diện theo dõi qua `buoc`/`tien_do` và gọi
 * `cham-lai` khi lượt chấm lỗi. Trợ lí và gợi ý tiêu chí (Mốc 7, 10–12 của đặc tả §16) chưa có endpoint.
 */
import { goiApi, goiApiTepCoTen } from '../api/http';
import { moLuongPost } from '../api/su-kien';
import type {
  BanDanhGia,
  BoTieuChi,
  BoTieuChiChiTiet,
  BoTieuChiSua,
  BoTieuChiTao,
  KetQuaCanTrongSo,
  KhoaTichHop,
  LuotApSua,
  LuotGoiY,
  LuotHoiDap,
  SoSanhPhien,
  KhoaTichHopTao,
  LinhVuc,
  LinhVucSua,
  LinhVucTao,
  LoaiHoSo,
  LoaiHoSoSua,
  LoaiHoSoTao,
  LuuNhap,
  KetQuaTieuChi,
  KhoaMoiTao,
  MucHangCho,
  NoiDungTep,
  NutLinhVucCay,
  Phien,
  PhienChiTiet,
  PhienTao,
  SuaKetQua,
  TongQuan,
  Trang,
  TrangThaiBo,
  TrangThaiPhien,
} from '../../modules/tham-dinh/types';

const GOC = '/api/v1/m/tham-dinh';

const ma = (id: string) => encodeURIComponent(id);

/** Tham số danh sách chung: `limit` 1–100, `cursor` của trang trước, `search` tìm theo tên. */
export interface ThamSoDanhSach {
  limit?: number;
  cursor?: string;
  search?: string;
}

export interface ThamSoLoaiHoSo extends ThamSoDanhSach {
  linh_vuc_id?: string;
}

export interface ThamSoBoTieuChi extends ThamSoDanhSach {
  loai_ho_so_id?: string;
  trang_thai?: TrangThaiBo;
}

export interface ThamSoPhien extends ThamSoDanhSach {
  trang_thai?: TrangThaiPhien | 'dang_xu_ly';
}

export const khoTamDinh = {
  /* ----- Lĩnh vực và loại hồ sơ (đặc tả mục 13.2) ------------------------------------------------- */

  danhSachLinhVuc: (q: ThamSoDanhSach = {}): Promise<Trang<LinhVuc>> =>
    goiApi(`${GOC}/linh-vuc`, { query: { ...q } }),

  linhVuc: (id: string): Promise<LinhVuc> => goiApi(`${GOC}/linh-vuc/${ma(id)}`),

  taoLinhVuc: (body: LinhVucTao): Promise<LinhVuc> =>
    goiApi(`${GOC}/linh-vuc`, { method: 'POST', body }),

  suaLinhVuc: (id: string, body: LinhVucSua): Promise<LinhVuc> =>
    goiApi(`${GOC}/linh-vuc/${ma(id)}`, { method: 'PATCH', body }),

  /** Còn loại hồ sơ thì backend trả 409 — ràng buộc của DB, không phải phép đếm trước khi xoá. */
  xoaLinhVuc: (id: string): Promise<void> =>
    goiApi(`${GOC}/linh-vuc/${ma(id)}`, { method: 'DELETE' }),

  danhSachLoaiHoSo: (q: ThamSoLoaiHoSo = {}): Promise<Trang<LoaiHoSo>> =>
    goiApi(`${GOC}/loai-ho-so`, { query: { ...q } }),

  loaiHoSo: (id: string): Promise<LoaiHoSo> => goiApi(`${GOC}/loai-ho-so/${ma(id)}`),

  taoLoaiHoSo: (body: LoaiHoSoTao): Promise<LoaiHoSo> =>
    goiApi(`${GOC}/loai-ho-so`, { method: 'POST', body }),

  suaLoaiHoSo: (id: string, body: LoaiHoSoSua): Promise<LoaiHoSo> =>
    goiApi(`${GOC}/loai-ho-so/${ma(id)}`, { method: 'PATCH', body }),

  xoaLoaiHoSo: (id: string): Promise<void> =>
    goiApi(`${GOC}/loai-ho-so/${ma(id)}`, { method: 'DELETE' }),

  /* ----- Bộ tiêu chí (đặc tả mục 13.3) ------------------------------------------------------------ */

  danhSachBo: (q: ThamSoBoTieuChi = {}): Promise<Trang<BoTieuChi>> =>
    goiApi(`${GOC}/bo-tieu-chi`, { query: { ...q } }),

  /** Cả thư viện một lượt: Lĩnh vực ▸ Loại ▸ Bộ. */
  cay: (): Promise<NutLinhVucCay[]> => goiApi(`${GOC}/bo-tieu-chi/cay`),

  bo: (id: string): Promise<BoTieuChiChiTiet> => goiApi(`${GOC}/bo-tieu-chi/${ma(id)}`),

  taoBo: (body: BoTieuChiTao): Promise<BoTieuChiChiTiet> =>
    goiApi(`${GOC}/bo-tieu-chi`, { method: 'POST', body }),

  suaBo: (id: string, body: BoTieuChiSua): Promise<BoTieuChiChiTiet> =>
    goiApi(`${GOC}/bo-tieu-chi/${ma(id)}`, { method: 'PATCH', body }),

  /** Đã phát hành thì backend trả 409 và mời chuyển sang Lưu trữ. */
  xoaBo: (id: string): Promise<void> =>
    goiApi(`${GOC}/bo-tieu-chi/${ma(id)}`, { method: 'DELETE' }),

  /**
   * Lưu bản nháp có **khoá lạc quan**: gửi kèm `cap_nhat_luc` mà người soạn đang nhìn. Lệch với máy chủ quá
   * 1 ms thì backend trả `409` kèm tên người vừa lưu (đặc tả mục 13.3) — người lưu sau không âm thầm xoá
   * công của người lưu trước.
   */
  luuNhap: (id: string, du: LuuNhap): Promise<BoTieuChiChiTiet> =>
    goiApi(`${GOC}/bo-tieu-chi/${ma(id)}/ban-nhap`, { method: 'PUT', body: du }),

  /** Mỗi lượt tạo một phiên bản mới, nên `POST` chứ không `PUT`. */
  phatHanh: (id: string, ghi_chu: string): Promise<BoTieuChiChiTiet> =>
    goiApi(`${GOC}/bo-tieu-chi/${ma(id)}/phat-hanh`, { method: 'POST', body: { ghi_chu } }),

  datCapDuyet: (id: string, cap_duyet: string[]): Promise<BoTieuChiChiTiet> =>
    goiApi(`${GOC}/bo-tieu-chi/${ma(id)}/cap-duyet`, { method: 'PUT', body: { cap_duyet } }),

  /* ----- Đợt sau: route chưa mở, gọi vào sẽ nhận 404 ---------------------------------------------- */

  tongQuan: (): Promise<TongQuan> => goiApi(`${GOC}/tong-quan`),

  danhSachPhien: (q: ThamSoPhien = {}): Promise<Trang<Phien>> =>
    goiApi(`${GOC}/phien`, { query: { ...q } }),

  phien: (id: string): Promise<PhienChiTiet> => goiApi(`${GOC}/phien/${ma(id)}`),

  /**
   * Nộp hồ sơ: multipart, nhiều tệp cùng tên trường `tep` (đặc tả mục 13.4).
   *
   * Không đặt `Content-Type` — trình duyệt tự thêm kèm `boundary`, đặt tay là gửi đi một biểu mẫu server
   * không tách được.
   */
  taoPhien: (du: PhienTao, tep: readonly File[]): Promise<PhienChiTiet> => {
    const bieuMau = new FormData();
    bieuMau.append('bo_id', du.bo_id);
    bieuMau.append('tieu_de', du.tieu_de);
    if (du.ma_tham_chieu) bieuMau.append('ma_tham_chieu', du.ma_tham_chieu);
    for (const t of tep) bieuMau.append('tep', t);
    return goiApi(`${GOC}/phien`, { method: 'POST', bieuMau });
  },

  xoaPhien: (id: string): Promise<void> => goiApi(`${GOC}/phien/${ma(id)}`, { method: 'DELETE' }),

  /** Kết quả từng tiêu chí, kèm cờ `can_xem` của luật đặc tả mục 10.1. */
  ketQuaPhien: (id: string): Promise<KetQuaTieuChi[]> => goiApi(`${GOC}/phien/${ma(id)}/ket-qua`),

  /**
   * Ghi đè phán quyết, ghi chú, đánh dấu đã xem — backend tính lại điểm cả phiên.
   *
   * Gửi `phan_quyet: null` là **gỡ** ghi đè, về phán quyết của AI; không gửi trường đó thì giữ nguyên.
   * Hai chuyện khác hẳn nhau, nên `undefined` phải được loại khỏi thân yêu cầu.
   */
  suaKetQua: (phienId: string, maTc: string, du: SuaKetQua): Promise<PhienChiTiet> =>
    goiApi(`${GOC}/phien/${ma(phienId)}/ket-qua/${ma(maTc)}`, { method: 'PATCH', body: du }),

  hoanTatPhien: (id: string): Promise<PhienChiTiet> =>
    goiApi(`${GOC}/phien/${ma(id)}/hoan-tat`, { method: 'POST' }),

  /**
   * Nộp lại hồ sơ: tạo một phiên mới trỏ về phiên này. `bo_id` lấy từ phiên trước ở backend.
   */
  nopLaiPhien: (id: string, tieu_de: string, tep: readonly File[]): Promise<PhienChiTiet> => {
    const bieuMau = new FormData();
    bieuMau.append('tieu_de', tieu_de);
    for (const t of tep) bieuMau.append('tep', t);
    return goiApi(`${GOC}/phien/${ma(id)}/nop-lai`, { method: 'POST', bieuMau });
  },

  /**
   * Tick đồng ý / từ chối một gợi ý sửa. `null` = bỏ quyết định (về `chua_quyet`).
   *
   * Đường riêng, **không** gộp vào `suaKetQua`: đường ấy chặn khi hồ sơ không ở bước rà soát, nhưng người ta
   * nộp bản sửa cả sau khi bị trả lại và sau khi hoàn tất (đặc tả mục 13.5).
   */
  quyetGoiYSua: (id: string, maTieuChi: string, dongY: boolean | null):
    Promise<{ ma: string; sua_trang_thai: string }> =>
    goiApi(`${GOC}/phien/${ma(id)}/goi-y-sua/${ma(maTieuChi)}`, { method: 'PATCH', body: { dongY } }),

  /** Một lượt áp gợi ý sửa, để poll. */
  docApSua: (id: string): Promise<LuotApSua> =>
    goiApi(`${GOC}/phien/${ma(id)}/ap-dung-sua`),

  /** Bấm "Áp dụng sửa": 202 rồi poll. Danh sách rỗng = mọi tiêu chí đang đồng ý. */
  apDungSua: (id: string, maTieuChi: readonly string[] = []): Promise<LuotApSua> =>
    goiApi(`${GOC}/phien/${ma(id)}/ap-dung-sua`, { method: 'POST', body: { ma: maTieuChi } }),

  /** Lịch sử hỏi–đáp **của chính người gọi** (đặc tả mục 10.5). */
  lichSuTroLy: (id: string): Promise<Trang<LuotHoiDap>> =>
    goiApi(`${GOC}/phien/${ma(id)}/tro-ly/lich-su`),

  /**
   * Hỏi trợ lí — **SSE**, mở bằng POST.
   *
   * Trả về generator khung và hàm dừng; bên gọi đọc từng khung và dựng câu trả lời dần. Không nối lại khi
   * đứt: nối lại là hỏi lại từ đầu và tiêu thêm một lượt gọi mô hình (xem `moLuongPost`).
   */
  hoiTroLy: (id: string, cauHoi: string) =>
    moLuongPost(`${GOC}/phien/${ma(id)}/tro-ly/hoi`, { cauHoi }),

  /** Bản đánh giá tổng thể; `trang_thai = 'chua_co'` khi chưa ai chạy lượt nào. */
  danhGiaPhien: (id: string): Promise<BanDanhGia> =>
    goiApi(`${GOC}/phien/${ma(id)}/danh-gia`),

  /** Viết lại bản đánh giá: 202 rồi poll `danhGiaPhien`. Đang chạy ⇒ 409. */
  vietLaiDanhGia: (id: string): Promise<BanDanhGia> =>
    goiApi(`${GOC}/phien/${ma(id)}/danh-gia/viet-lai`, { method: 'POST' }),

  /** So sánh với lần nộp trước; lần nộp đầu ⇒ 409 `THAM_DINH_SESSION_NO_PREVIOUS`. */
  soSanhPhien: (id: string): Promise<SoSanhPhien> =>
    goiApi(`${GOC}/phien/${ma(id)}/so-sanh`),

  /** Chạy thử bộ tiêu chí theo **bản nháp**; trả về phiên `thu_nghiem` để theo dõi như hồ sơ thường. */
  chayThuBo: (boId: string, tieu_de: string, tep: readonly File[]): Promise<PhienChiTiet> => {
    const bieuMau = new FormData();
    bieuMau.append('tieu_de', tieu_de);
    for (const t of tep) bieuMau.append('tep', t);
    return goiApi(`${GOC}/bo-tieu-chi/${ma(boId)}/chay-thu`, { method: 'POST', bieuMau });
  },

  /**
   * Chấm lại một hồ sơ: sinh lượt chấm mới và đẩy lại vào hàng đợi. Chấm theo **bản chụp đã nộp**, không
   * theo bộ tiêu chí hiện tại — nên nó không phải "nộp lại".
   */
  chamLaiPhien: (id: string): Promise<PhienChiTiet> =>
    goiApi(`${GOC}/phien/${ma(id)}/cham-lai`, { method: 'POST' }),

  pheDuyetPhien: (id: string, y_kien: string): Promise<void> =>
    goiApi(`${GOC}/phien/${ma(id)}/phe-duyet`, { method: 'POST', body: { y_kien } }),

  /** Ý kiến **bắt buộc** khi trả lại — người thẩm định cần biết sửa gì. */
  traLaiPhien: (id: string, y_kien: string): Promise<void> =>
    goiApi(`${GOC}/phien/${ma(id)}/tra-lai`, { method: 'POST', body: { y_kien } }),

  /**
   * Biên bản thẩm định .docx. Trả cả tên tệp vì backend đặt tên (có dấu tiếng Việt, theo tiêu đề hồ sơ) —
   * để trình duyệt tự đặt theo URL thì tệp tải về mang tên là UUID của phiên.
   *
   * Hồ sơ chưa hoàn tất vẫn xuất được; biên bản khi đó mang nhãn BẢN NHÁP.
   */
  bienBanPhien: (id: string, tenMacDinh: string): Promise<{ blob: Blob; ten: string }> =>
    goiApiTepCoTen(`${GOC}/phien/${ma(id)}/bien-ban`, tenMacDinh),

  /**
   * Mở một lượt AI gợi ý tiêu chí: **202** rồi poll. Multipart vì có tệp mẫu tuỳ chọn.
   *
   * `bieuMau` thay vì `body`: `goiApi` không đặt `Content-Type` cho nó, để trình duyệt tự thêm `boundary`
   * của `FormData` — đặt tay là gửi một multipart thiếu boundary.
   */
  batDauGoiY: (id: string, mo_ta: string, tep?: File): Promise<LuotGoiY> => {
    const bieuMau = new FormData();
    bieuMau.append('mo_ta', mo_ta);
    if (tep) bieuMau.append('tep', tep);
    return goiApi(`${GOC}/bo-tieu-chi/${ma(id)}/goi-y`, { method: 'POST', bieuMau });
  },

  /** Poll một lượt gợi ý. */
  docGoiY: (id: string, luotId: string): Promise<LuotGoiY> =>
    goiApi(`${GOC}/bo-tieu-chi/${ma(id)}/goi-y/${ma(luotId)}`),

  /**
   * AI cân trọng số cho bản nháp. **Đồng bộ** (10–20 giây) và backend không ghi gì — kết quả là đề xuất,
   * người soạn xem rồi lưu bằng `luuNhap` như mọi lần sửa khác.
   */
  canTrongSo: (id: string): Promise<KetQuaCanTrongSo> =>
    goiApi(`${GOC}/bo-tieu-chi/${ma(id)}/can-trong-so`, { method: 'POST' }),

  /** Văn bản đã trích của một tệp — thứ mô hình đọc và giao diện tô sáng. */
  noiDungTep: (phienId: string, tepId: string): Promise<NoiDungTep> =>
    goiApi(`${GOC}/phien/${ma(phienId)}/tep/${ma(tepId)}/noi-dung`),

  /**
   * Gửi một sự kiện mẫu tới webhook của khoá, trả về đúng câu kết quả (mã HTTP và phản hồi).
   *
   * Không ném khi bên nhận từ chối: câu kết quả là thứ người cấu hình cần đọc, kể cả khi thất bại.
   */
  thuWebhook: (id: string): Promise<{ ket_qua: string }> =>
    goiApi(`${GOC}/tich-hop/khoa/${ma(id)}/thu`, { method: 'POST' }),

  hangChoPheDuyet: (): Promise<Trang<MucHangCho>> => goiApi(`${GOC}/phe-duyet/hang-cho`),

  danhSachKhoa: (): Promise<Trang<KhoaTichHop>> => goiApi(`${GOC}/tich-hop/khoa`),

  /** Phản hồi mang `khoa` và `bi_mat_webhook` **nguyên văn** — lần duy nhất người dùng thấy chúng. */
  taoKhoa: (body: KhoaTichHopTao): Promise<KhoaMoiTao> =>
    goiApi(`${GOC}/tich-hop/khoa`, { method: 'POST', body }),

  /** Thu hồi, không xoá: phiên đã gửi bằng khoá này vẫn phải truy được nguồn. */
  thuHoiKhoa: (id: string): Promise<void> =>
    goiApi(`${GOC}/tich-hop/khoa/${ma(id)}`, { method: 'DELETE' }),
};
